import type { TimeEntryDocType, TimeEntryType } from '@ast-data/shared';
import { DEFAULT_TIME_ENTRY_TYPE } from '@ast-data/shared';
import { defineStoredJson } from '../../utils/storage';

/**
 * Local (unsynced) draft time-entry rows for the field grid. New field rows live
 * here — persisted to localStorage so an accidental reload / going offline never
 * loses in-progress entries — until they're complete and the user taps Save,
 * which promotes them into RxDB (where they sync). Keyed per project + workday +
 * Zeiterfassung (Mitarbeiter / Geräte), so the two day grids never see each
 * other's drafts.
 */

type DraftStore = Record<string, TimeEntryDocType[]>;

/**
 * Unsynced work, so a wipe leaves it alone. Renamed from the unprefixed
 * `timeEntryDrafts` when every key in the app moved to one prefix — a draft
 * typed on a tablet before the rename is carried over on the first read.
 */
const draftStore = defineStoredJson<DraftStore>({
    key: 'time_entry_drafts',
    legacyKeys: ['timeEntryDrafts'],
    fallback: () => ({}),
    keepOnWipe: true,
    decode: (parsed) =>
        parsed && typeof parsed === 'object' && !Array.isArray(parsed)
            ? (parsed as DraftStore)
            : undefined
});

function storeKey(
    projectId: string,
    day: string,
    entryType: TimeEntryType
): string {
    // The worker key keeps its original shape so drafts left in a browser from
    // before the Geräte-Zeiterfassung are still found.
    return entryType === DEFAULT_TIME_ENTRY_TYPE
        ? `${projectId}__${day}`
        : `${projectId}__${entryType}__${day}`;
}

function readAll(): DraftStore {
    return draftStore.read();
}

function writeAll(store: DraftStore): void {
    draftStore.write(store);
}

/**
 * The draft rows for one project + day + variant (empty when none). `entryType`
 * and `quantity` are stamped on the way out: a draft left in the browser from
 * before the Geräte-Zeiterfassung (or before the Anzahl) has neither, and the
 * schema requires both the moment the row is promoted into RxDB.
 */
export function loadDrafts(
    projectId: string,
    day: string,
    entryType: TimeEntryType
): TimeEntryDocType[] {
    const drafts = readAll()[storeKey(projectId, day, entryType)] ?? [];
    return drafts.map((draft) => ({
        ...draft,
        entryType,
        quantity: Number(draft.quantity) || 1
    }));
}

/** Replace one project + day + variant's drafts (drops the bucket when empty). */
export function saveDrafts(
    projectId: string,
    day: string,
    entryType: TimeEntryType,
    drafts: TimeEntryDocType[]
): void {
    const store = readAll();
    const key = storeKey(projectId, day, entryType);
    if (drafts.length > 0) {
        store[key] = drafts;
    } else {
        delete store[key];
    }
    writeAll(store);
}
